import React, { useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  LinearProgress,
  Menu,
  ListItemButton,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import ShareIcon from "@mui/icons-material/Share";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SecurityIcon from "@mui/icons-material/Security";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import LockIcon from "@mui/icons-material/Lock";
import ScienceIcon from "@mui/icons-material/Science";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ReceiptIcon from "@mui/icons-material/Receipt";
import AssignmentIcon from "@mui/icons-material/Assignment";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import { useDropzone } from "react-dropzone";

const DocumentManagerPage = () => {
  const [documents, setDocuments] = useState([
    // Mock data for demonstration
    {
      id: 1,
      name: "Biopsy_Report_2024.pdf",
      category: "Medical Records",
      size: 2458624,
      uploadDate: new Date("2024-01-15"),
      fileType: "pdf",
      encrypted: true,
      shared: [],
    },
    {
      id: 2,
      name: "NT-proBNP_Results_Dec.pdf",
      category: "Lab Results",
      size: 524288,
      uploadDate: new Date("2024-12-10"),
      fileType: "pdf",
      encrypted: true,
      shared: ["Dr. Smith"],
    },
    {
      id: 3,
      name: "Insurance_Card_Front.jpg",
      category: "Insurance",
      size: 1048576,
      uploadDate: new Date("2024-11-01"),
      fileType: "image",
      encrypted: true,
      shared: [],
    },
  ]);

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuDocument, setMenuDocument] = useState(null);

  const [uploadForm, setUploadForm] = useState({
    category: "",
    description: "",
    files: [],
  });

  const categories = [
    {
      value: "Medical Records",
      label: "Medical Records",
      icon: <LocalHospitalIcon />,
      color: "#e91e63",
    },
    {
      value: "Lab Results",
      label: "Lab Results",
      icon: <ScienceIcon />,
      color: "#9c27b0",
    },
    {
      value: "Prescriptions",
      label: "Prescriptions",
      icon: <VaccinesIcon />,
      color: "#3f51b5",
    },
    {
      value: "Insurance",
      label: "Insurance",
      icon: <ReceiptIcon />,
      color: "#2196f3",
    },
    {
      value: "Trial Documents",
      label: "Trial Documents",
      icon: <AssignmentIcon />,
      color: "#009688",
    },
    {
      value: "Imaging",
      label: "Imaging (X-ray, MRI, CT)",
      icon: <MonitorHeartIcon />,
      color: "#ff9800",
    },
    {
      value: "Other",
      label: "Other Documents",
      icon: <DescriptionIcon />,
      color: "#795548",
    },
  ];

  const onDrop = useCallback((acceptedFiles) => {
    setUploadForm((prev) => ({
      ...prev,
      files: [...prev.files, ...acceptedFiles],
    }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg", ".gif"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    },
    maxSize: 10485760, // 10MB
  });

  const handleUploadDialogOpen = () => {
    setUploadDialogOpen(true);
  };

  const handleUploadDialogClose = () => {
    setUploadDialogOpen(false);
    setUploadForm({ category: "", description: "", files: [] });
    setUploadProgress(0);
  };

  const handleUpload = async () => {
    if (!uploadForm.category || uploadForm.files.length === 0) {
      alert("Please select a category and at least one file");
      return;
    }

    setUploading(true);

    // Simulate upload progress
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 200);

    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 2500));

    // Add uploaded files to documents list
    const newDocuments = uploadForm.files.map((file, index) => ({
      id: documents.length + index + 1,
      name: file.name,
      category: uploadForm.category,
      size: file.size,
      uploadDate: new Date(),
      fileType: file.type.startsWith("image/")
        ? "image"
        : file.type.includes("pdf")
        ? "pdf"
        : "document",
      encrypted: true,
      shared: [],
      description: uploadForm.description,
    }));

    setDocuments((prev) => [...prev, ...newDocuments]);
    setUploading(false);
    handleUploadDialogClose();

    alert(`Successfully uploaded ${uploadForm.files.length} file(s)!`);
  };

  const handleRemoveFile = (index) => {
    setUploadForm((prev) => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index),
    }));
  };

  const handleDeleteDocument = (docId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this document? This action cannot be undone."
      )
    ) {
      setDocuments((prev) => prev.filter((doc) => doc.id !== docId));
      handleMenuClose();
    }
  };

  const handleDownloadDocument = (doc) => {
    // TODO: Implement actual download
    alert(`Downloading ${doc.name}...`);
    handleMenuClose();
  };

  const handleShareDialogOpen = (doc) => {
    setSelectedDocument(doc);
    setShareDialogOpen(true);
    handleMenuClose();
  };

  const handleShareDialogClose = () => {
    setShareDialogOpen(false);
    setSelectedDocument(null);
  };

  const handleShare = (email) => {
    if (!email) {
      alert("Please enter an email address");
      return;
    }

    // TODO: Implement actual sharing
    setDocuments((prev) =>
      prev.map((doc) =>
        doc.id === selectedDocument.id
          ? { ...doc, shared: [...doc.shared, email] }
          : doc
      )
    );

    alert(`Document shared with ${email}`);
    handleShareDialogClose();
  };

  const handleMenuOpen = (event, doc) => {
    setAnchorEl(event.currentTarget);
    setMenuDocument(doc);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuDocument(null);
  };

  const handleExportPackage = () => {
    const selectedCategory = categories[activeTab].value;
    const categoryDocs = documents.filter(
      (doc) => activeTab === 0 || doc.category === selectedCategory
    );

    // TODO: Implement actual export/zip
    alert(
      `Exporting ${categoryDocs.length} documents from "${
        activeTab === 0 ? "All Documents" : selectedCategory
      }" category...`
    );
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "pdf":
        return <PictureAsPdfIcon sx={{ color: "#d32f2f" }} />;
      case "image":
        return <ImageIcon sx={{ color: "#1976d2" }} />;
      default:
        return <DescriptionIcon sx={{ color: "#757575" }} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  const formatDate = (date) => {
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch =
      !searchTerm ||
      doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.category.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      activeTab === 0 || doc.category === categories[activeTab - 1].value;

    return matchesSearch && matchesCategory;
  });

  const getCategoryStats = () => {
    return categories.map((cat) => ({
      ...cat,
      count: documents.filter((doc) => doc.category === cat.value).length,
    }));
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
          <FolderIcon sx={{ fontSize: 40, color: "primary.main" }} />
          <Typography variant="h3" fontWeight="bold">
            Document Manager
          </Typography>
          <Chip label="Premium" color="warning" icon={<CheckCircleIcon />} />
        </Box>
        <Typography variant="h6" color="text.secondary">
          Securely store and manage your medical records
        </Typography>
      </Box>

      {/* HIPAA Compliance Alert */}
      <Alert severity="success" icon={<SecurityIcon />} sx={{ mb: 3 }}>
        <Typography variant="subtitle2" gutterBottom>
          <strong>HIPAA Compliant & Encrypted</strong>
        </Typography>
        <Typography variant="body2">
          All documents are encrypted end-to-end (AES-256) and stored on
          HIPAA-compliant servers. Your data is protected and only accessible by
          you and those you explicitly authorize.
        </Typography>
      </Alert>

      <Grid container spacing={3}>
        {/* Left Column - Categories & Stats */}
        <Grid item xs={12} md={3}>
          {/* Upload Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            startIcon={<CloudUploadIcon />}
            onClick={handleUploadDialogOpen}
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
              py: 1.5,
              mb: 2,
            }}
          >
            Upload Documents
          </Button>

          {/* Storage Info */}
          <Card elevation={2} sx={{ mb: 2 }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Storage Used
              </Typography>
              <Box
                sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}
              >
                <Typography variant="h4" fontWeight="bold">
                  {formatFileSize(
                    documents.reduce((sum, doc) => sum + doc.size, 0)
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  / 5 GB
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  (documents.reduce((sum, doc) => sum + doc.size, 0) /
                    (5 * 1024 * 1024 * 1024)) *
                  100
                }
                sx={{ height: 8, borderRadius: 4 }}
              />
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {documents.length} documents stored
              </Typography>
            </CardContent>
          </Card>

          {/* Categories List */}
          <Paper elevation={2}>
            <List>
              <ListItem>
                <Typography variant="subtitle2" fontWeight="bold">
                  Categories
                </Typography>
              </ListItem>
              <Divider />
              {getCategoryStats().map((cat, index) => (
                <ListItemButton
                  key={cat.value}
                  selected={activeTab === index + 1}
                  onClick={() => setActiveTab(index + 1)}
                >
                  <ListItemIcon sx={{ color: cat.color }}>
                    {cat.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={cat.label}
                    secondary={`${cat.count} files`}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Right Column - Documents */}
        <Grid item xs={12} md={9}>
          <Paper elevation={3}>
            {/* Search & Actions */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search documents..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleExportPackage}
                    >
                      Export Package
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Tabs */}
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab label={`All Documents (${documents.length})`} />
              {categories.map((cat) => (
                <Tab
                  key={cat.value}
                  label={`${cat.label} (${
                    documents.filter((d) => d.category === cat.value).length
                  })`}
                />
              ))}
            </Tabs>

            {/* Documents Table */}
            {filteredDocuments.length > 0 ? (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width={50}></TableCell>
                      <TableCell>
                        <strong>Name</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Category</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Size</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Uploaded</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Shared With</strong>
                      </TableCell>
                      <TableCell width={100}>
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredDocuments.map((doc) => (
                      <TableRow key={doc.id} hover>
                        <TableCell>{getFileIcon(doc.fileType)}</TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography variant="body2">{doc.name}</Typography>
                            {doc.encrypted && (
                              <Tooltip title="Encrypted">
                                <LockIcon fontSize="small" color="success" />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={doc.category}
                            size="small"
                            sx={{
                              bgcolor: `${
                                categories.find((c) => c.value === doc.category)
                                  ?.color
                              }15`,
                              color: categories.find(
                                (c) => c.value === doc.category
                              )?.color,
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatFileSize(doc.size)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(doc.uploadDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {doc.shared.length > 0 ? (
                            <Chip
                              label={`${doc.shared.length} person(s)`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Not shared
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, doc)}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: "center", py: 8, px: 4 }}>
                <FolderIcon
                  sx={{ fontSize: 80, color: "text.disabled", mb: 2 }}
                />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  No documents found
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {searchTerm
                    ? "Try adjusting your search"
                    : "Upload your first document to get started"}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  onClick={handleUploadDialogOpen}
                  sx={{
                    background:
                      "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
                  }}
                >
                  Upload Documents
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Upload Dialog */}
      <Dialog
        open={uploadDialogOpen}
        onClose={handleUploadDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CloudUploadIcon color="primary" />
            Upload Documents
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Document Category</InputLabel>
                <Select
                  value={uploadForm.category}
                  label="Document Category"
                  onChange={(e) =>
                    setUploadForm((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {cat.icon}
                        {cat.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description (Optional)"
                multiline
                rows={2}
                value={uploadForm.description}
                onChange={(e) =>
                  setUploadForm((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Add notes about this document..."
              />
            </Grid>

            <Grid item xs={12}>
              <Paper
                {...getRootProps()}
                sx={{
                  p: 3,
                  textAlign: "center",
                  border: "2px dashed",
                  borderColor: isDragActive ? "primary.main" : "divider",
                  bgcolor: isDragActive
                    ? "rgba(102, 126, 234, 0.05)"
                    : "background.default",
                  cursor: "pointer",
                  transition: "all 0.3s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "rgba(102, 126, 234, 0.05)",
                  },
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
                />
                <Typography variant="body1" gutterBottom>
                  {isDragActive
                    ? "Drop files here..."
                    : "Drag & drop files here, or click to select"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Supported: PDF, JPG, PNG, DOC, DOCX (Max 10MB per file)
                </Typography>
              </Paper>
            </Grid>

            {uploadForm.files.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files ({uploadForm.files.length})
                </Typography>
                <List dense>
                  {uploadForm.files.map((file, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton
                          edge="end"
                          onClick={() => handleRemoveFile(index)}
                          disabled={uploading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemIcon>
                        {file.type.startsWith("image/") ? (
                          <ImageIcon color="primary" />
                        ) : file.type.includes("pdf") ? (
                          <PictureAsPdfIcon color="error" />
                        ) : (
                          <DescriptionIcon />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={file.name}
                        secondary={formatFileSize(file.size)}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>
            )}

            {uploading && (
              <Grid item xs={12}>
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2">Uploading...</Typography>
                    <Typography variant="body2">{uploadProgress}%</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                  />
                </Box>
              </Grid>
            )}

            <Grid item xs={12}>
              <Alert severity="info" icon={<SecurityIcon />}>
                <Typography variant="caption">
                  All files are encrypted during upload and stored securely on
                  HIPAA-compliant servers.
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUploadDialogClose} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            variant="contained"
            disabled={
              !uploadForm.category || uploadForm.files.length === 0 || uploading
            }
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            }}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={handleShareDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Share Document</DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Sharing: <strong>{selectedDocument.name}</strong>
                </Typography>
              </Alert>

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                placeholder="doctor@hospital.com"
                helperText="Enter the email of the healthcare provider or trial coordinator"
                sx={{ mb: 2 }}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleShare(e.target.value);
                  }
                }}
              />

              {selectedDocument.shared.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Currently Shared With:
                  </Typography>
                  <List dense>
                    {selectedDocument.shared.map((email, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={email} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="caption">
                  The recipient will receive a secure link to view this
                  document. Access can be revoked at any time.
                </Typography>
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleShareDialogClose}>Cancel</Button>
          <Button
            onClick={(e) => {
              const email = e.target
                .closest("div")
                .parentElement.querySelector('input[type="email"]').value;
              handleShare(email);
            }}
            variant="contained"
            sx={{
              background: "linear-gradient(135deg, #1A5EA8 0%, #0B2545 100%)",
            }}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => menuDocument && handleDownloadDocument(menuDocument)}
        >
          <ListItemIcon>
            <DownloadIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => menuDocument && alert("Preview coming soon!")}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => menuDocument && handleShareDialogOpen(menuDocument)}
        >
          <ListItemIcon>
            <ShareIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Share</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => menuDocument && handleDeleteDocument(menuDocument.id)}
          sx={{ color: "error.main" }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Disclaimer */}
      <Paper elevation={1} sx={{ mt: 4, p: 3, bgcolor: "grey.50" }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Security & Privacy:</strong> Your documents are protected with
          bank-level encryption (AES-256) and stored on HIPAA-compliant AWS
          servers. We never share your data without your explicit permission.
          You can delete your documents at any time.
        </Typography>
      </Paper>
    </Container>
  );
};

export default DocumentManagerPage;
